import axios from 'axios';
import { dedupeInflight } from '../utils/dedupedFetch';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Let the runtime set multipart boundary (default JSON Content-Type breaks file uploads)
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    // Log request in development
    if (process.env.NODE_ENV === 'development') {
      console.log('🚀 API Request:', config.method?.toUpperCase(), config.url);
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    // Log response in development
    if (process.env.NODE_ENV === 'development') {
      console.log('✅ API Response:', response.status, response.config.url);
    }
    return response;
  },
  (error) => {
    // Log error in development
    if (process.env.NODE_ENV === 'development') {
      console.error('❌ API Error:', error.response?.status, error.config?.url, error.message);
    }
    
    // Handle common errors
    if (error.response?.status === 401) {
      // Unauthorized - clear token and redirect to login
      localStorage.removeItem('authToken');
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    
    return Promise.reject(error);
  }
);

// Error handler utility
export const handleApiError = (error) => {
  if (error.response) {
    // Server responded with error status
    const { status, data } = error.response;
    
    switch (status) {
      case 400:
        return { message: data.error || 'Bad request', type: 'error' };
      case 401:
        return { message: 'Unauthorized access', type: 'error' };
      case 403:
        return { message: 'Access forbidden', type: 'error' };
      case 404:
        return { message: 'Resource not found', type: 'error' };
      case 500:
        return { message: 'Internal server error', type: 'error' };
      default:
        return { message: data.error || 'Something went wrong', type: 'error' };
    }
  } else if (error.request) {
    // Network error
    return { message: 'Network error - please check your connection', type: 'error' };
  } else {
    // Other error
    return { message: error.message || 'Something went wrong', type: 'error' };
  }
};

// API status checker
export const checkApiHealth = async () => {
  try {
    const response = await api.get('/health');
    return response.data.status === 'OK';
  } catch (error) {
    return false;
  }
};

// User authentication methods
export const authAPI = {
  // Send OTP to mobile number
  sendOTP: async (mobile) => {
    const response = await api.post('/api/auth/send-otp', { mobile });
    return response.data;
  },

  /** OTP for new registration after name/email (existing users use sendOTP only). */
  sendSignupOTP: async (mobile) => {
    const response = await api.post('/api/auth/send-signup-otp', { mobile });
    return response.data;
  },

  // Verify OTP and login/register user
  verifyOTP: async (mobile, otp, name = null, email = null) => {
    const payload = { mobile, otp };
    if (name) payload.name = name;
    if (email) payload.email = email;
    
    const response = await api.post('/api/auth/verify-otp', payload);
    return response.data;
  },

  // Get user profile
  getProfile: () =>
    dedupeInflight(
      'auth:profile',
      async () => {
        const response = await api.get('/api/auth/profile');
        return response.data;
      },
      { cacheMs: 15000 }
    ),

  // Update user profile
  updateProfile: async (profileData) => {
    const response = await api.put('/api/auth/profile', profileData);
    return response.data;
  },

  // Logout user
  logout: async () => {
    const response = await api.post('/api/auth/logout');
    return response.data;
  }
};

// User management methods
export const userAPI = {
  // Get user details by ID
  getUserById: async (userId) => {
    const response = await api.get(`/api/users/${userId}`);
    return response.data;
  },

  // Update user details
  updateUser: async (userId, userData) => {
    const response = await api.put(`/api/users/${userId}`, userData);
    return response.data;
  },

  // Create dog profile for user
  createDogProfile: async (userId, dogProfileData) => {
    const response = await api.post(`/api/users/${userId}/dog-profiles`, dogProfileData);
    return response.data;
  }
};

// Booking management methods
export const bookingAPI = {
  // Get user's bookings
  getMyBookings: (limit = 20, offset = 0) =>
    dedupeInflight(
      `bookings:my:${limit}:${offset}`,
      async () => {
        const response = await api.get(`/api/bookings/my-bookings?limit=${limit}&offset=${offset}`);
        return response.data;
      },
      { cacheMs: 15000 }
    ),

  // Get booking by ID
  getBookingById: (bookingId) => {
    if (!bookingId) {
      return Promise.reject(new Error('bookingId is required'));
    }
    return dedupeInflight(
      `bookings:by-id:${bookingId}`,
      async () => {
        const response = await api.get(`/api/bookings/${bookingId}`);
        return response.data;
      },
      { cacheMs: 30000 }
    );
  },

  // Create new booking
  createBooking: async (bookingData) => {
    const response = await api.post('/api/bookings', bookingData);
    return response.data;
  },

  // Cancel booking
  cancelBooking: async (bookingId) => {
    const response = await api.delete(`/api/bookings/${bookingId}`);
    return response.data;
  }
};

// Event booking management methods
export const eventBookingAPI = {
  // Get user's event bookings
  getMyEventBookings: (limit = 20, offset = 0) =>
    dedupeInflight(
      `event-bookings:my:${limit}:${offset}`,
      async () => {
        const response = await api.get(
          `/api/event-bookings/my-bookings?limit=${limit}&offset=${offset}`
        );
        return response.data;
      },
      { cacheMs: 15000 }
    ),

  // Get event booking by ID
  getEventBookingById: async (bookingId) => {
    const response = await api.get(`/api/event-bookings/${bookingId}`);
    return response.data;
  },

  // Create new event booking
  createEventBooking: async (bookingData) => {
    const response = await api.post('/api/event-bookings', bookingData);
    return response.data;
  },

  // Cancel event booking
  cancelEventBooking: async (bookingId) => {
    const response = await api.delete(`/api/event-bookings/${bookingId}`);
    return response.data;
  }
};

export const wishlistAPI = {
  getWishlist: async () => {
    const response = await api.get('/api/wishlist');
    return response.data;
  },

  addToWishlist: async (itemData) => {
    const response = await api.post('/api/wishlist', itemData);
    return response.data;
  },

  removeFromWishlist: async (type, id) => {
    const response = await api.delete(`/api/wishlist/${type}/${id}`);
    return response.data;
  }
};

// Payment management methods
export const paymentAPI = {
  // Initiate payment (creates booking and payment session)
  initiatePayment: async (bookingData) => {
    const response = await api.post('/api/payments/initiate', bookingData);
    return response.data;
  },

  initiateEventPayment: async (payload) => {
    const response = await api.post('/api/payments/initiate-event', payload);
    return response.data;
  },

  // Verify payment status
  verifyPayment: (orderId) => {
    if (!orderId) {
      return Promise.reject(new Error('orderId is required'));
    }
    return dedupeInflight(
      `payments:verify:${orderId}`,
      async () => {
        const response = await api.get(`/api/payments/verify/${orderId}`);
        return response.data;
      },
      { cacheMs: 10000 }
    );
  },

  abandonStayCheckout: async (bookingId) => {
    const response = await api.post(`/api/payments/abandon-stay/${bookingId}`);
    return response.data;
  },

  // Get payments for a booking
  getBookingPayments: async (bookingId) => {
    const response = await api.get(`/api/payments/booking/${bookingId}`);
    return response.data;
  },

  // Get payment by ID
  getPaymentById: async (paymentId) => {
    const response = await api.get(`/api/payments/${paymentId}`);
    return response.data;
  }
};

// Diary management methods
export const diaryAPI = {
  // Get diary entries for a user
  getDiaryEntries: async (userId) => {
    const response = await api.get(`/api/users/${userId}/diary-entries`);
    return response.data;
  },

  // Create new diary entry
  createDiaryEntry: async (userId, diaryData) => {
    const response = await api.post(`/api/users/${userId}/diary-entries`, diaryData);
    return response.data;
  },

  // Update diary entry
  updateDiaryEntry: async (userId, entryId, diaryData) => {
    const response = await api.put(`/api/users/${userId}/diary-entries/${entryId}`, diaryData);
    return response.data;
  },

  // Delete diary entry
  deleteDiaryEntry: async (userId, entryId) => {
    const response = await api.delete(`/api/users/${userId}/diary-entries/${entryId}`);
    return response.data;
  }
};

export const contactAPI = {
  submit: async (formData) => {
    const response = await api.post('/api/contact', formData);
    return response.data;
  },
};

export default api; 